export type Plan = 'free' | 'pro' | 'caregiver' | 'enterprise'
export type BillingPeriod = 'monthly' | 'annual'
export type SubscriptionStatus = 'active' | 'trialing' | 'past_due' | 'cancelled' | 'expired' | 'paused'
export type PaymentProvider = 'paystack' | 'stripe' | 'paddle'

export interface Subscription {
  userId?: string
  plan: Plan
  billingPeriod?: BillingPeriod
  status: SubscriptionStatus
  provider?: PaymentProvider
  countryCode?: string
  trialEndsAt?: string
  currentPeriodEnd?: string
  cancelledAt?: string
  pausedAt?: string
  resumeAt?: string
  createdAt?: string
  updatedAt?: string
  // Corporate wellness — set when user belongs to an organisation
  organisationId?: string
  organisationName?: string
  organisationSlug?: string
}

export interface CheckoutResponse {
  checkoutUrl: string
  transactionId?: string // Provider-specific: Stripe checkout session ID or Paddle transaction ID
  provider: PaymentProvider
  currency?: string
  amount?: number
  formattedPrice?: string
}

export type CancelReason = 'too_expensive' | 'not_using' | 'missing_features' | 'other'

// From AIML service: GET /v1/usage (counters + limits from Redis, timeline from Postgres)
export interface AIMLUsageStats {
  chatMessagesToday: number
  chatMessagesLimit: number | null
  voiceThisMonth: number
  voiceLimit: number | null
  handoffsThisMonth: number
  handoffsLimit: number | null
  timelineDataPoints: number
  timelineFirstDate: string | null
}

// From Identity service: GET /v1/subscription/usage (only what identity owns)
export interface IdentityUsageStats {
  connectedDevices: number
}

// Combined for UI consumption
export interface UsageStats extends AIMLUsageStats, IdentityUsageStats {}

export interface PlanFeature {
  label: string
  included: boolean
}

export interface PlanOption {
  id: Plan
  name: string
  tagline: string
  monthlyPrice: number
  annualMonthlyPrice: number
  annualPrice: number
  features: string[]
  highlight?: boolean
}

// Backend plan catalog response from GET /v1/subscription/plans
export interface PlanCatalogFeature {
  name: string
  description: string
  highlight?: boolean
}

export interface PlanCatalogLimits {
  chatMessagesPerDay: number | null
  voiceSessionsPerMonth: number | null
  handoffsPerMonth: number | null
  handoffShares: number | null
  handoffConversations: number | null
  timelineDays: number | null
}

export interface PlanCatalogPricing {
  currency: string           // e.g. "GBP"
  currencySymbol: string     // e.g. "£"
  monthlyPrice: number       // e.g. 9.99
  annualMonthlyPrice: number // e.g. 7.99
  annualPrice: number        // e.g. 95.88
}

export interface PlanCatalogItem {
  id: Plan
  name: string
  tagline: string
  pricing?: PlanCatalogPricing
  limits: PlanCatalogLimits
  features: PlanCatalogFeature[]
}

export interface PlanComparisonRow {
  feature: string
  category: string
  free: string
  pro: string
  caregiver: string
}

export interface PlanCatalog {
  plans: PlanCatalogItem[]
  comparison: PlanComparisonRow[]
}

// Plan config for UI display.
// Limits must match 006-plan-feature-gating.md — the backend is the authority.
// TODO: Replace with GET /v1/subscription/plans endpoint so limits come from the backend.
export const PLANS: PlanOption[] = [
  {
    id: 'pro',
    name: 'Pro',
    tagline: 'Nothing missed. Nothing overlooked.',
    monthlyPrice: 9.99,
    annualMonthlyPrice: 7.99,
    annualPrice: 95.88,
    highlight: true,
    features: [
      'Unlimited conversations — your questions never stop',
      'Full daily briefing — trends, anomalies, nothing overlooked',
      'Unlimited voice — the 2am safety net, always there',
      'Complete health timeline — the full picture, not just 30 days',
      'Advanced lab intelligence — what your results mean over time',
      'Unlimited Handoff + PDF — every appointment, fully prepared',
      'Weekly & monthly reports — patterns you\'d never spot alone',
    ],
  },
  {
    id: 'caregiver',
    name: 'Caregiver',
    tagline: 'Everyone you care about, covered.',
    monthlyPrice: 24.99,
    annualMonthlyPrice: 19.99,
    annualPrice: 239.88,
    highlight: false,
    features: [
      'Everything in Pro, plus:',
      '3\u20135 health profiles — one account for everyone',
      'Cross-profile alerts — nothing missed for anyone',
      'Multi-provider coordination — every doctor, one view',
      'Per-profile trend analysis — each person, fully understood',
      'Cross-profile reports — the full family picture',
    ],
  },
]

export const FREE_FEATURES: string[] = [
  '15 conversations a day — real answers, not search results',
  'Daily briefing — how you\'re doing, in plain language',
  '3 voice conversations a month — talk when you need to',
  'Unlimited device connections — connect everything you track',
  '30-day timeline — your recent health, one place',
  'See what\'s coming — before it arrives',
  '2 Handoff preps a month — walk in prepared',
]

// Plan hierarchy for feature gating
export function planLevel(plan: Plan): number {
  switch (plan) {
    case 'free': return 0
    case 'pro': return 1
    case 'caregiver': return 2
    case 'enterprise': return 2  // Enterprise gets Pro-level features (same as caregiver tier)
  }
}

// Feature comparison data — grouped by product area (not alphabetical).
// Limits must match 006-plan-feature-gating.md — the backend is the authority.
// TODO: Replace with GET /v1/subscription/plans endpoint.
export interface ComparisonFeature {
  label: string
  free: string | boolean
  pro: string | boolean
  caregiver: string | boolean
}

export interface ComparisonGroup {
  name: string
  features: ComparisonFeature[]
}

export const FEATURE_COMPARISON: ComparisonGroup[] = [
  {
    name: 'Ask Treatwise',
    features: [
      { label: 'Health conversations', free: '15/day', pro: 'Unlimited', caregiver: 'Unlimited' },
      { label: 'Voice conversations', free: '3/month', pro: 'Unlimited', caregiver: 'Unlimited' },
    ],
  },
  {
    name: 'Pulse',
    features: [
      { label: 'Daily briefing', free: 'Basic summary', pro: 'Full + trends + anomalies', caregiver: 'Full per profile' },
      { label: 'Weekly & monthly reports', free: false, pro: true, caregiver: 'Per profile' },
      { label: 'Cross-profile alerts', free: false, pro: false, caregiver: true },
    ],
  },
  {
    name: 'Timeline',
    features: [
      { label: 'Health timeline', free: '30 days', pro: 'Full history', caregiver: 'Full per profile' },
      { label: 'Data export', free: false, pro: true, caregiver: true },
    ],
  },
  {
    name: 'Handoff',
    features: [
      { label: 'Doctor prep', free: '2/month', pro: 'Unlimited', caregiver: 'Unlimited' },
      { label: 'Conversation depth', free: 'Last 5 chats', pro: 'Full history', caregiver: 'Full per profile' },
      { label: 'Shares', free: '1 total', pro: 'Unlimited', caregiver: 'Unlimited' },
      { label: 'PDF export', free: false, pro: true, caregiver: true },
      { label: 'Multi-provider coordination', free: false, pro: false, caregiver: true },
    ],
  },
  {
    name: 'Devices & Data',
    features: [
      { label: 'Device connections', free: 'Unlimited', pro: 'Unlimited', caregiver: 'Unlimited' },
      { label: 'Lab results intelligence', free: 'Basic', pro: 'Historical tracking + trends', caregiver: 'Per-profile tracking' },
    ],
  },
  {
    name: 'Ahead',
    features: [
      { label: 'Preventive care', free: true, pro: true, caregiver: true },
      { label: 'AI forecasting', free: false, pro: true, caregiver: 'Per profile' },
    ],
  },
  {
    name: 'Profiles',
    features: [
      { label: 'Health profiles', free: '1', pro: '1', caregiver: '3\u20135' },
    ],
  },
]
